import videojs from 'video.js';
import { getRandomId } from '../../utils/helpers.js';

const Plugin = videojs.getPlugin('plugin');

const defaultSettings = {
    breakOverlay: {
        display: false,
        text(episode) {
            return episode.overlayText;
        },
        style: {},
    },
    episodeTime(episode) {
        return episode.time;
    },
    episodes: [],
};

// hh:mm:ss
const timeToSeconds = time => {
    const secondsInMinute = 60;
    const secondsInHour = secondsInMinute ** 2;
    const timeArray = time.split(':');

    if (timeArray.length === 3) {
        return timeArray[0] * secondsInHour + timeArray[1] * secondsInMinute + Number(timeArray[2]);
    }

    return timeArray[0] * 60 + Number(timeArray[1]);
};

const createEl = (tagName, props, attrs) => {
    const el = document.createElement(tagName, props);

    props.className?.forEach(item => {
        el.classList.add(item);
    });

    if (props.innerHTML) el.innerHTML = props.innerHTML;

    if (attrs) {
        Object.keys(attrs).forEach(key => {
            el.setAttribute(key, attrs[key]);
        });
    }

    return el;
};

const sortEpisodesList = list => {
    list.sort((a, b) => a.time - b.time);
};

class VideoEpisodes extends Plugin {
    #NULL_INDEX = -1;

    #episodesList = [];

    #currentEpisodeIndex = this.#NULL_INDEX;

    #breakOverlay = null;

    #overlayIndex = this.#NULL_INDEX;

    #settings;

    constructor(player, options) {
        super(player, options);

        this.player = player;
        this.#settings = videojs.mergeOptions(defaultSettings, options);

        this.videoJsEpisodesPlugin(options);
    }

    prev() {
        const currentTime = this.player.currentTime();

        for (let i = this.#episodesList.length - 1; i >= 0; i--) {
            const episodeTime = this.#episodesList[i].time;

            if (episodeTime + 0.5 < currentTime) {
                this.player.currentTime(episodeTime);

                return;
            }
        }
    }

    removeAll() {
        const indexArray = [];

        for (let i = 0; i < this.#episodesList.length; i++) {
            indexArray.push(i);
        }

        this.#removeEpisodes(indexArray);
    }

    reset(newEpisodes) {
        this.removeAll();
        this.#addEpisodes(newEpisodes);
    }

    #addEpisodes = newEpisodes => {
        newEpisodes.forEach(episode => {
            episode.key = getRandomId();

            this.player.el().querySelector('.vjs-progress-holder')
                .appendChild(this.#createEpisodeDiv(episode));

            this.#episodesList.push(episode);
        });

        sortEpisodesList(this.#episodesList);
    };

    #removeEpisodes = indexArray => {
        if (this.#breakOverlay) {
            this.#overlayIndex = this.#NULL_INDEX;
            this.#breakOverlay.style.visibility = 'hidden';
        }

        this.#currentEpisodeIndex = this.#NULL_INDEX;

        const deleteIndexList = [];

        indexArray.forEach(index => {
            const episode = this.#episodesList[index];

            if (episode) {
                deleteIndexList.push(index);

                const el = this.player.el().querySelector(`.vjs-episode[data-episode-key='${episode.key}']`);

                el.parentNode.removeChild(el);
            }
        });

        deleteIndexList.reverse();
        deleteIndexList.forEach(deleteIndex => {
            this.#episodesList.splice(deleteIndex, 1);
        });

        sortEpisodesList(this.#episodesList);
    };

    clearBreakOverlay = () => {
        this.#overlayIndex = this.#NULL_INDEX;

        if (this.#breakOverlay) {
            this.#breakOverlay.style.visibility = 'hidden';
        }
    };

    setNewBreakOverlay = currentEpisode => {
        if (this.#overlayIndex !== this.#currentEpisodeIndex) {
            this.#overlayIndex = this.#currentEpisodeIndex;

            if (this.#breakOverlay) {
                this.#breakOverlay.querySelector('.vjs-overlay-content').innerHTML = this.#settings.breakOverlay.text(currentEpisode);
            }
        }

        if (this.#breakOverlay) {
            this.#breakOverlay.style.visibility = 'visible';
        }
    };

    #updateBreakOverlay = () => {
        if (!this.#settings.breakOverlay.display) return;

        if (this.#currentEpisodeIndex < 0) {
            return this.clearBreakOverlay();
        }

        const currentTime = this.player.currentTime();
        const currentEpisode = this.#episodesList[this.#currentEpisodeIndex];

        if (currentTime >= currentEpisode.time) {
            this.setNewBreakOverlay(currentEpisode);
        } else {
            this.clearBreakOverlay();
        }
    };

    #createEpisodeDiv = episode => {
        const episodeDiv = createEl('div', {}, {
            'data-episode-key': episode.key,
            'data-episode-time': episode.time,
        });

        this.#setEpisodeDivStyle(episode, episodeDiv);

        return episodeDiv;
    };

    // eslint-disable-next-line max-statements
    #setEpisodeDivStyle = (episode, episodeDiv) => {
        episodeDiv.className = `vjs-episode ${episode.class || ''}`;

        const ratio = episode.time / this.player.duration();

        if (ratio < 0 || ratio > 1) {
            episodeDiv.style.display = 'none';
        }

        const getEpisodePosition = () => (episode.time / this.player.duration()) * 100;

        episodeDiv.style.left = `${getEpisodePosition()}%`;

        if (episode.duration) {
            episodeDiv.style.width = `${(episode.duration / this.player.duration()) * 100}%`;
            episodeDiv.style.marginLeft = '0px';
        } else {
            const episodeDivBounding = episodeDiv.getBoundingClientRect();

            episodeDiv.style.marginLeft = `${episodeDivBounding.width / 2}px`;
        }
    };

    #registerEpisodeTipHandler = () => {
        const progressControlElement = this.player.el().querySelector('.vjs-progress-control');
        const getEpisodeByTime = time => this.#episodesList.filter(item => time >= item.time)?.pop();

        const findChildComponent = (array, componentName) => {
            if (array) {
                for (let i = 0; i < array.length; i++) {
                    if (array[i].options().name === componentName) {
                        return array[i];
                    }

                    const childrenByName = findChildComponent(array[i].children(), componentName);

                    if (childrenByName) return childrenByName;
                }
            }

            return null;
        };

        const timeTooltip = findChildComponent([this.player.controlBar], 'TimeTooltip');

        progressControlElement.addEventListener('mousemove', () => {
            if (!this.#episodesList.length) return;

            // eslint-disable-next-line func-names
            timeTooltip.update = function(seekBarRect, seekBarPoint, time) {
                const episode = getEpisodeByTime(timeToSeconds(time));

                if (!episode) return;

                timeTooltip.contentEl().innerHTML = `<div class="vjs-tooltip-title">${episode.text}</div> ${time}`;
            };
        });
    };

    // eslint-disable-next-line max-lines-per-function, max-statements
    videoJsEpisodesPlugin = () => {
        const initializeOverlay = () => {
            this.#breakOverlay = createEl('div', {
                className: ['vjs-overlay'],
                innerHTML: '<div class=\'vjs-overlay-content\'></div>',
            });

            this.#breakOverlay.addEventListener('click', () => {
                this.prev();
            });

            Object.keys(this.#settings.breakOverlay.style).forEach(key => {
                if (this.#breakOverlay) {
                    this.#breakOverlay.style[key] = this.#settings.breakOverlay.style[key];
                }
            });
            this.player.el().appendChild(this.#breakOverlay);
            this.#overlayIndex = this.#NULL_INDEX;
        };

        const getNextEpisodeTime = index => {
            if (index < this.#episodesList.length - 1) {
                return this.#episodesList[index + 1].time;
            }

            return this.player.duration();
        };

        // eslint-disable-next-line max-statements, complexity,
        const onUpdateEpisode = () => {
            if (!this.#episodesList.length) return;

            let nextEpisodeTime = this.#NULL_INDEX;
            let newEpisodeIndex = this.#NULL_INDEX;

            const currentTime = this.player.currentTime();

            if (this.#currentEpisodeIndex !== this.#NULL_INDEX) {
                nextEpisodeTime = getNextEpisodeTime(this.#currentEpisodeIndex);

                if (currentTime >= this.#episodesList[this.#currentEpisodeIndex].time && currentTime < nextEpisodeTime) return;

                if (this.#currentEpisodeIndex === this.#episodesList.length - 1 && currentTime === this.player.duration()) return;
            }

            if (currentTime < this.#episodesList[0].time) {
                newEpisodeIndex = this.#NULL_INDEX;
            } else {
                for (let i = 0; i < this.#episodesList.length; i++) {
                    nextEpisodeTime = getNextEpisodeTime(i);

                    if (currentTime >= this.#episodesList[i].time && currentTime < nextEpisodeTime) {
                        newEpisodeIndex = i;
                        break;
                    }
                }
            }

            if (newEpisodeIndex !== this.#currentEpisodeIndex) {
                this.#currentEpisodeIndex = newEpisodeIndex;
                this.player.trigger('episodes:change', {
                    value: newEpisodeIndex,
                });
            }
        };

        const onTimeUpdate = () => {
            onUpdateEpisode();
            this.#updateBreakOverlay();
        };

        const initialize = () => {
            this.removeAll();
            this.#addEpisodes(this.#settings.episodes);
            this.#registerEpisodeTipHandler();

            if (this.#settings.breakOverlay.display) initializeOverlay();

            onTimeUpdate();
            this.player.on('timeupdate', onTimeUpdate);
            this.player.off('loadedmetadata');
        };

        this.player.on('loadedmetadata', () => {
            initialize();
        });
    };
}

videojs.registerPlugin('episodes', VideoEpisodes);
